import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Panel, fmt, fmtDate } from "./DashboardPrimitives";
import InvoiceStatus from "src/components/InvoiceStatus/InvoiceStatus";
import { TbCircleCheck, TbAlertTriangle, TbAlertOctagon, TbArrowRight } from "react-icons/tb";
import type { Invoice } from "src/types";

const PREVIEW_COUNT = 4;

/** Same gross/remaining formula as useDashboard.ts's totalOutstanding KPI, kept in sync so
 *  this card's total always agrees with the hero stat above it. */
function remainingOf(inv: Invoice): number {
    const net = (inv.amount ?? 0) + (inv.materialCosts ?? 0);
    const vr = inv.vatRate ?? 0;
    const gross = (vr > 0 ? net * (1 + vr / 100) : net) + (inv.dunningFineAmount ?? 0);
    return Math.max(0, gross - (inv.paidAmount ?? 0));
}

const CLOSED = new Set([2 /* paid */, 5 /* writtenOff */, 6 /* deleted */]);

/** "Offene Posten" — open items: sent invoices with money still outstanding, most urgent
 *  (highest dunning level, then oldest) first. Built entirely from data useDashboard() already
 *  fetches (/invoices?minimal=1) for the hero KPIs — no separate query. Structurally mirrors
 *  FinApiPanel (title+subtitle, total, 3-stat row, fixed-height preview list, view-all button)
 *  so the two cards stay visually symmetrical side by side regardless of how much data either
 *  one actually has. */
export function OpenPostenCard({ invoices }: { invoices: Invoice[] }) {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const openInvoices = useMemo(() => {
        return invoices
            .filter((inv) => !!inv.sentDate && !CLOSED.has(inv.status) && remainingOf(inv) > 0.005)
            .sort((a, b) => (b.dunning ?? 0) - (a.dunning ?? 0) || (a.sentDate ?? "").localeCompare(b.sentDate ?? ""));
    }, [invoices]);

    const noReminderCount = openInvoices.filter((inv) => (inv.dunning ?? 0) === 0).length;
    const remindedCount   = openInvoices.filter((inv) => (inv.dunning ?? 0) >= 1 && (inv.dunning ?? 0) <= 2).length;
    const criticalCount   = openInvoices.filter((inv) => (inv.dunning ?? 0) >= 3).length;

    const preview = openInvoices.slice(0, PREVIEW_COUNT);
    const placeholderCount = Math.max(0, PREVIEW_COUNT - preview.length);
    const totalOutstanding = openInvoices.reduce((s, inv) => s + remainingOf(inv), 0);

    return (
        <Panel className="finapi-panel finapi-panel--summary">
            <div className="section-header">
                <div className="finapi-panel-header">
                    <h3 className="section-header__title">{t("dashboard.openPosten")}</h3>
                    <span className="finapi-panel-iban">
                        {t("dashboard.openPostenCount", { count: openInvoices.length })}
                    </span>
                </div>
            </div>

            <div className="finapi-balance">
                <span className="finapi-balance__label">{t("dashboard.openPostenTotal")}</span>
                <span className="finapi-balance__value">{fmt(totalOutstanding)}</span>
            </div>

            <div className="finapi-stats finapi-stats--summary">
                <div className="finapi-stat-card">
                    <span className="finapi-stat-card__icon finapi-stat-card__icon--auto"><TbCircleCheck /></span>
                    <div className="finapi-stat-card__body">
                        <span className="finapi-stat-card__value">{noReminderCount}</span>
                        <span className="finapi-stat-card__label">{t("dashboard.openPostenNoReminder")}</span>
                    </div>
                </div>
                <div className="finapi-stat-card">
                    <span className="finapi-stat-card__icon finapi-stat-card__icon--warn"><TbAlertTriangle /></span>
                    <div className="finapi-stat-card__body">
                        <span className="finapi-stat-card__value">{remindedCount}</span>
                        <span className="finapi-stat-card__label">{t("dashboard.openPostenReminded")}</span>
                    </div>
                </div>
                <div className="finapi-stat-card">
                    <span className="finapi-stat-card__icon finapi-stat-card__icon--unmatched"><TbAlertOctagon /></span>
                    <div className="finapi-stat-card__body">
                        <span className="finapi-stat-card__value">{criticalCount}</span>
                        <span className="finapi-stat-card__label">{t("dashboard.openPostenCritical")}</span>
                    </div>
                </div>
            </div>

            <div className="finapi-panel__preview">
                <div className="finapi-panel__preview-list">
                    {preview.length === 0 && (
                        <div className="finapi-panel__preview-row finapi-panel__preview-row--placeholder">
                            <span className="finapi-panel__preview-counterpart finapi-panel__preview-counterpart--muted">
                                {t("dashboard.openPostenEmpty")}
                            </span>
                        </div>
                    )}
                    {preview.map((inv) => {
                        const patientName = inv.patient
                            ? `${inv.patient.firstName ?? ""} ${inv.patient.lastName ?? ""}`.trim()
                            : inv.recipient
                                ? `${inv.recipient.firstName ?? ""} ${inv.recipient.lastName ?? ""}`.trim()
                                : "—";
                        return (
                            <div key={inv.id} className="open-posten-row" onClick={() => navigate(`/invoices/${inv.id}`)}>
                                <span className="finapi-panel__preview-date">{inv.sentDate ? fmtDate(inv.sentDate) : "—"}</span>
                                <span className="open-posten-row__id">{inv.readableId}</span>
                                <span className="open-posten-row__name">{patientName}</span>
                                <InvoiceStatus status={inv.status} dunning={inv.dunning} unsent={!inv.sentDate} emailFailed={inv.emailFailed} />
                                <span className="open-posten-row__amount">{fmt(remainingOf(inv))}</span>
                            </div>
                        );
                    })}
                    {Array.from({ length: preview.length === 0 ? placeholderCount - 1 : placeholderCount }).map((_, i) => (
                        <div key={`placeholder-${i}`} className="finapi-panel__preview-row finapi-panel__preview-row--placeholder" aria-hidden />
                    ))}
                </div>
                <div className="finapi-panel__preview-fade" aria-hidden />
            </div>

            <button type="button" className="finapi-panel__view-all" onClick={() => navigate("/invoices", { state: { viewKey: "open" } })}>
                {t("finapi.viewAll")} <TbArrowRight />
            </button>
        </Panel>
    );
}
